import { FormEvent, useCallback, useEffect, useState } from "react";

import {
  AdminApiError,
  AdminContext,
  AmbassadorInvitationSummary,
  AuditEntry,
  CandidateReview,
  CreateAmbassadorInvitationParams,
  GameDiagnostic,
  IssuedInvitation,
  KillSwitchView,
  SupportProjection,
  beginEnrollment,
  bootstrap,
  clearMemoryOnlySession,
  confirmEnrollment,
  createAmbassadorInvitation,
  getAmbassadorCandidateReview,
  listAmbassadorInvitations,
  listKillSwitches,
  loadAudit,
  loadContext,
  loadGameDiagnostic,
  login,
  logout,
  lookup,
  overrideKillSwitch,
  recoverMfa,
  revokeAmbassadorInvitation,
  revokeSession,
  stepUp,
  suspendAccount,
} from "./api";
import { adminEnvironment } from "./env";
import "./styles.css";

type State =
  "loading" | "unauthenticated" | "authenticated" | "denied" | "degraded";

export function App(): React.JSX.Element {
  const [state, setState] = useState<State>("loading");
  const [context, setContext] = useState<AdminContext>();
  const [error, setError] = useState<string>();

  async function restore(): Promise<void> {
    try {
      await bootstrap();
      const value = await loadContext();
      setContext(value);
      setState("authenticated");
    } catch (failure) {
      clearMemoryOnlySession();
      setState(
        failure instanceof AdminApiError && failure.status === 403
          ? "denied"
          : "unauthenticated",
      );
    }
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void restore();
    }, 0);
    return () => window.clearTimeout(timer);
  }, []);

  async function authenticate(
    email: string,
    password: string,
    totp: string,
  ): Promise<void> {
    setError(undefined);
    try {
      await login(email, password, totp);
      setContext(await loadContext());
      setState("authenticated");
    } catch (failure) {
      setError(
        failure instanceof AdminApiError
          ? failure.message
          : "Admin authentication is unavailable.",
      );
      setState("unauthenticated");
    }
  }

  async function endSession(): Promise<void> {
    await logout().catch(() => undefined);
    setContext(undefined);
    setState("unauthenticated");
  }

  return (
    <div className="app-shell">
      <header>
        <div>
          <p className="eyebrow">Operational Control Center</p>
          <h1>Zhatura Admin</h1>
        </div>
        <strong
          className={`environment environment-${adminEnvironment.VITE_DEPLOYMENT_ENVIRONMENT.toLowerCase()}`}
        >
          {adminEnvironment.VITE_DEPLOYMENT_ENVIRONMENT}
        </strong>
      </header>
      <main>
        {state === "loading" && (
          <Status message="Restoring the Admin Session…" />
        )}
        {state === "unauthenticated" && (
          <LoginForm onSubmit={authenticate} error={error} />
        )}
        {state === "denied" && (
          <Status message="Current workforce authority does not permit this surface." />
        )}
        {state === "degraded" && (
          <Status message="Canonical Admin services are temporarily unavailable." />
        )}
        {state === "authenticated" && context !== undefined && (
          <AuthenticatedShell
            context={context}
            onLogout={endSession}
            onDegraded={() => setState("degraded")}
          />
        )}
      </main>
    </div>
  );
}

function LoginForm({
  onSubmit,
  error,
}: {
  onSubmit: (email: string, password: string, totp: string) => Promise<void>;
  error: string | undefined;
}): React.JSX.Element {
  const [pending, setPending] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setPending(true);
    await onSubmit(
      field(form, "email"),
      field(form, "password"),
      field(form, "totp"),
    );
    setPending(false);
  }
  return (
    <section className="panel narrow" aria-labelledby="login-title">
      <h2 id="login-title">Workforce sign in</h2>
      <p>
        Password and current authenticator code are required. Consumer Sessions
        are not accepted.
      </p>
      <form
        onSubmit={(event) => {
          void submit(event);
        }}
      >
        <label>
          Email
          <input name="email" type="email" autoComplete="username" required />
        </label>
        <label>
          Password
          <input
            name="password"
            type="password"
            autoComplete="current-password"
            required
          />
        </label>
        <label>
          Authenticator code
          <input
            name="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            required
          />
        </label>
        {error !== undefined && (
          <p role="alert" className="error">
            {error}
          </p>
        )}
        <button type="submit" disabled={pending}>
          {pending ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <EnrollmentRecovery />
    </section>
  );
}

function EnrollmentRecovery(): React.JSX.Element {
  const [message, setMessage] = useState<string>();
  async function enroll(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const uri = await beginEnrollment(
      field(form, "email"),
      field(form, "password"),
      field(form, "enrollment"),
    );
    setMessage(`Authenticator provisioning URI ready: ${uri}`);
  }
  async function confirm(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const codes = await confirmEnrollment(
      field(form, "enrollment"),
      field(form, "totp"),
    );
    setMessage(`Store these recovery codes once: ${codes.join(" ")}`);
  }
  async function recover(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const credential = await recoverMfa(
      field(form, "email"),
      field(form, "password"),
      field(form, "recovery"),
    );
    setMessage(
      `Recovery accepted. Replacement enrollment credential: ${credential}`,
    );
  }
  return (
    <details>
      <summary>MFA enrollment or recovery</summary>
      <form onSubmit={(event) => void enroll(event)}>
        <label>
          Email
          <input name="email" type="email" required />
        </label>
        <label>
          Password
          <input name="password" type="password" required />
        </label>
        <label>
          Enrollment credential
          <input name="enrollment" type="password" required />
        </label>
        <button type="submit">Begin enrollment</button>
      </form>
      <form onSubmit={(event) => void confirm(event)}>
        <label>
          Enrollment credential
          <input name="enrollment" type="password" required />
        </label>
        <label>
          Authenticator code
          <input name="totp" pattern="[0-9]{6}" required />
        </label>
        <button type="submit">Confirm enrollment</button>
      </form>
      <form onSubmit={(event) => void recover(event)}>
        <label>
          Email
          <input name="email" type="email" required />
        </label>
        <label>
          Password
          <input name="password" type="password" required />
        </label>
        <label>
          Recovery code
          <input name="recovery" type="password" required />
        </label>
        <button type="submit">Recover MFA</button>
      </form>
      {message !== undefined && <p role="status">{message}</p>}
    </details>
  );
}

function AuthenticatedShell({
  context,
  onLogout,
  onDegraded,
}: {
  context: AdminContext;
  onLogout: () => Promise<void>;
  onDegraded: () => void;
}): React.JSX.Element {
  const [tab, setTab] = useState<
    "support" | "ambassador" | "organizations" | "kill-switches"
  >("support");

  return (
    <div className="authenticated-shell">
      <nav className="nav-tabs" aria-label="Workforce workspace navigation">
        <button
          type="button"
          className={`tab-btn ${tab === "support" ? "active" : ""}`}
          onClick={() => setTab("support")}
        >
          Exact-ID Support
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === "ambassador" ? "active" : ""}`}
          onClick={() => setTab("ambassador")}
        >
          Ambassador Candidate Journeys
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === "organizations" ? "active" : ""}`}
          onClick={() => setTab("organizations")}
        >
          Academies & Organizations
        </button>
        <button
          type="button"
          className={`tab-btn ${tab === "kill-switches" ? "active" : ""}`}
          onClick={() => setTab("kill-switches")}
        >
          Kill Switches
        </button>
      </nav>
      {tab === "support" && (
        <SupportWorkspace
          context={context}
          onLogout={onLogout}
          onDegraded={onDegraded}
        />
      )}
      {tab === "ambassador" && (
        <AmbassadorWorkspace
          context={context}
          onLogout={onLogout}
          onDegraded={onDegraded}
        />
      )}
      {tab === "organizations" && (
        <OrganizationWorkspace
          context={context}
          onLogout={onLogout}
          onDegraded={onDegraded}
        />
      )}
      {tab === "kill-switches" && (
        <KillSwitchesWorkspace
          context={context}
          onLogout={onLogout}
          onDegraded={onDegraded}
        />
      )}
    </div>
  );
}

function SupportWorkspace({
  context,
  onLogout,
  onDegraded,
}: {
  context: AdminContext;
  onLogout: () => Promise<void>;
  onDegraded: () => void;
}): React.JSX.Element {
  const [result, setResult] = useState<SupportProjection>();
  const [audit, setAudit] = useState<readonly AuditEntry[]>([]);
  const [game, setGame] = useState<GameDiagnostic>();
  const [error, setError] = useState<string>();
  async function search(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      setResult(await lookup(field(form, "kind"), field(form, "id")));
      setError(undefined);
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503)
        onDegraded();
      else
        setError(
          failure instanceof AdminApiError ? failure.message : "Lookup failed.",
        );
    }
  }
  async function revoke(): Promise<void> {
    if (result?.target_kind !== "SESSION") return;
    const reason = window.prompt("Reason for Session revocation");
    if (reason === null || !window.confirm("Revoke this exact Session?"))
      return;
    await revokeSession(result.target_id, reason);
    setResult({ ...result, status: "ADMIN_SUPPORT" });
  }
  async function elevate(): Promise<void> {
    const code = window.prompt("Current authenticator code");
    if (code !== null) await stepUp(code);
  }
  async function showAudit(): Promise<void> {
    if (result !== undefined) setAudit(await loadAudit(result.target_id));
  }
  async function suspend(): Promise<void> {
    if (result?.target_kind !== "ACCOUNT" || result.version === null) return;
    const reason = window.prompt("Reason for Account suspension");
    if (reason === null || !window.confirm("Suspend this exact Account?"))
      return;
    await suspendAccount(result.target_id, result.version, reason);
    setResult({ ...result, status: "SUSPENDED", version: result.version + 1 });
  }
  async function inspectGame(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      setGame(await loadGameDiagnostic(field(form, "game-id")));
      setError(undefined);
    } catch (failure) {
      setGame(undefined);
      setError(
        failure instanceof AdminApiError
          ? failure.message
          : "Game diagnostic failed.",
      );
    }
  }
  return (
    <section className="panel" aria-labelledby="support-title">
      <div className="toolbar">
        <div>
          <h2 id="support-title">Exact-ID support</h2>
          <p>Signed in as {context.person_id}</p>
        </div>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            void onLogout();
          }}
        >
          Log out
        </button>
      </div>
      <form
        className="lookup"
        onSubmit={(event) => {
          void search(event);
        }}
      >
        <label>
          Target type
          <select name="kind">
            <option>PERSON</option>
            <option>ACCOUNT</option>
            <option>SESSION</option>
            <option>PLAYER</option>
          </select>
        </label>
        <label>
          Exact UUID
          <input name="id" required pattern="[0-9a-fA-F-]{36}" />
        </label>
        <button type="submit">Look up</button>
      </form>
      {error !== undefined && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {result !== undefined && (
        <article className="result">
          <h3>{result.target_kind}</h3>
          <dl>
            <dt>ID</dt>
            <dd>{result.target_id}</dd>
            <dt>Status</dt>
            <dd>{result.status}</dd>
            <dt>Label</dt>
            <dd>{result.display_label ?? "Not exposed"}</dd>
            <dt>Bounded detail</dt>
            <dd>{result.detail_status ?? "Not exposed"}</dd>
          </dl>
          {result.target_kind === "SESSION" && (
            <button
              type="button"
              className="danger"
              onClick={() => {
                void revoke();
              }}
            >
              Revoke Session
            </button>
          )}
          {result.target_kind === "ACCOUNT" && (
            <button
              type="button"
              className="danger"
              onClick={() => void suspend()}
            >
              Suspend Account
            </button>
          )}
          <button
            type="button"
            className="secondary"
            onClick={() => void elevate()}
          >
            Refresh privileged assurance
          </button>
          <button
            type="button"
            className="secondary"
            onClick={() => void showAudit()}
          >
            View bounded Audit
          </button>
          {audit.length > 0 && (
            <ul aria-label="Bounded Audit evidence">
              {audit.map((entry) => (
                <li key={entry.id}>
                  {entry.source}: {entry.action} — {entry.outcome}
                </li>
              ))}
            </ul>
          )}
        </article>
      )}
      <hr />
      {context.capabilities.includes("admin.chess.game.read") ? (
        <section aria-labelledby="game-diagnostic-title">
          <h3 id="game-diagnostic-title">Exact Game diagnostic</h3>
          <p>Read-only canonical integrity and lifecycle information.</p>
          <form
            className="lookup"
            onSubmit={(event) => {
              void inspectGame(event);
            }}
          >
            <label>
              Exact Game UUID
              <input name="game-id" required pattern="[0-9a-fA-F-]{36}" />
            </label>
            <button type="submit">Inspect Game</button>
          </form>
          {game !== undefined && (
            <article className="result" aria-label="Game diagnostic result">
              <dl>
                <dt>Game</dt>
                <dd>{game.game_id}</dd>
                <dt>Lifecycle</dt>
                <dd>{game.lifecycle}</dd>
                <dt>Result</dt>
                <dd>{game.result ?? "Not terminal"}</dd>
                <dt>Termination</dt>
                <dd>{game.termination ?? "Not terminal"}</dd>
                <dt>Sequence / version</dt>
                <dd>
                  {game.sequence} / {game.version}
                </dd>
                <dt>Ruleset</dt>
                <dd>{game.ruleset}</dd>
                <dt>Reconstruction integrity</dt>
                <dd>{game.integrity_status}</dd>
                <dt>Created</dt>
                <dd>{game.created_at}</dd>
                <dt>Started</dt>
                <dd>{game.started_at ?? "Not started"}</dd>
                <dt>Completed</dt>
                <dd>{game.completed_at ?? "Not completed"}</dd>
              </dl>
              <ul aria-label="Game participants">
                {game.participants.map((participant) => (
                  <li key={participant.player_profile_id}>
                    {participant.side}: {participant.display_name} (
                    {participant.player_profile_id})
                  </li>
                ))}
              </ul>
            </article>
          )}
        </section>
      ) : (
        <p>Game diagnostics are not available to this workforce Actor.</p>
      )}
    </section>
  );
}

function AmbassadorWorkspace({
  context,
  onLogout,
  onDegraded,
}: {
  context: AdminContext;
  onLogout: () => Promise<void>;
  onDegraded: () => void;
}): React.JSX.Element {
  const [invitations, setInvitations] = useState<
    readonly AmbassadorInvitationSummary[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [issued, setIssued] = useState<IssuedInvitation>();
  const [copied, setCopied] = useState(false);
  const [review, setReview] = useState<CandidateReview>();
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const loadInvitations = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await listAmbassadorInvitations();
      setInvitations(data);
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to load invitations.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [onDegraded]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadInvitations();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadInvitations]);

  async function handleIssue(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setNotice(undefined);
    const form = new FormData(event.currentTarget);
    const candidateName = field(form, "candidate_name");
    const contactEmail = field(form, "contact_email") || undefined;
    const programKey =
      field(form, "program_key") || "ambassador_candidate_journey";
    const proposedRole = (field(form, "proposed_role") ||
      undefined) as CreateAmbassadorInvitationParams["proposed_role"];
    const proposedType = (field(form, "proposed_primary_type") ||
      undefined) as CreateAmbassadorInvitationParams["proposed_primary_type"];
    const territoryCode = field(form, "territory_code") || "GLOBAL";
    const expiryHoursRaw = field(form, "expiry_hours");
    const expiryHours = expiryHoursRaw ? parseInt(expiryHoursRaw, 10) : 168;

    try {
      const result = await createAmbassadorInvitation({
        candidate_given_name: candidateName,
        contact_email: contactEmail,
        program_key: programKey,
        proposed_role: proposedRole,
        proposed_primary_type: proposedType,
        territory_code: territoryCode,
        expiry_hours: expiryHours,
      });
      setIssued(result);
      setCopied(false);
      setNotice(`Invitation issued successfully for ${candidateName}.`);
      void loadInvitations();
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to issue invitation.",
        );
      }
    }
  }

  async function handleCopy(): Promise<void> {
    if (!issued?.invitation_url) return;
    try {
      await navigator.clipboard.writeText(issued.invitation_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 3000);
    } catch {
      // Clipboard unavailable
    }
  }

  async function inspectCandidate(invitationId: string): Promise<void> {
    setReviewLoading(true);
    setError(undefined);
    try {
      const data = await getAmbassadorCandidateReview(invitationId);
      setReview(data);
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to load candidate review.",
        );
      }
    } finally {
      setReviewLoading(false);
    }
  }

  async function handleRevoke(
    invitationId: string,
    version: number,
  ): Promise<void> {
    const reason = window.prompt("Reason for revoking this invitation:");
    if (
      reason === null ||
      reason.trim() === "" ||
      !window.confirm(
        "Confirm revocation of this invitation? This action cannot be undone.",
      )
    ) {
      return;
    }
    setError(undefined);
    try {
      await revokeAmbassadorInvitation(invitationId, reason.trim(), version);
      setNotice(`Invitation ${invitationId} revoked.`);
      void loadInvitations();
      if (review?.invitation_id === invitationId) {
        void inspectCandidate(invitationId);
      }
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to revoke invitation.",
        );
      }
    }
  }

  return (
    <section className="panel" aria-labelledby="ambassador-title">
      <div className="toolbar">
        <div>
          <h2 id="ambassador-title">Ambassador Candidate Journeys</h2>
          <p>Signed in as {context.person_id}</p>
        </div>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            void onLogout();
          }}
        >
          Log out
        </button>
      </div>

      {notice !== undefined && (
        <p
          role="status"
          className="notice"
          style={{ color: "#34d399", marginTop: "1rem" }}
        >
          {notice}
        </p>
      )}
      {error !== undefined && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      <div className="grid-2">
        <section aria-labelledby="issue-invitation-title">
          <h3 id="issue-invitation-title">Issue Candidate Invitation</h3>
          <p>
            Issue an invitation to start an Ambassador qualification journey.
          </p>
          <form
            onSubmit={(e) => {
              void handleIssue(e);
            }}
          >
            <label>
              Candidate Given Name
              <input name="candidate_name" required placeholder="e.g. Magnus" />
            </label>
            <label>
              Contact Email
              <input
                name="contact_email"
                type="email"
                placeholder="candidate@example.com"
              />
            </label>
            <label>
              Program Key
              <input
                name="program_key"
                defaultValue="ambassador_candidate_journey"
                required
              />
            </label>
            <label>
              Proposed Network Role
              <select name="proposed_role" defaultValue="AMBASSADOR">
                <option value="AMBASSADOR">AMBASSADOR</option>
                <option value="GROWTH_PARTNER">GROWTH_PARTNER</option>
                <option value="GROWTH_LEADER">GROWTH_LEADER</option>
              </select>
            </label>
            <label>
              Proposed Primary Type
              <select name="proposed_primary_type" defaultValue="COMMUNITY">
                <option value="COMMUNITY">COMMUNITY</option>
                <option value="PLAYER">PLAYER</option>
                <option value="PARENT">PARENT</option>
                <option value="COACH">COACH</option>
                <option value="ACADEMY">ACADEMY</option>
                <option value="EDUCATION">EDUCATION</option>
                <option value="CREATOR">CREATOR</option>
                <option value="INFLUENCE">INFLUENCE</option>
              </select>
            </label>
            <label>
              Territory Code
              <input name="territory_code" defaultValue="GLOBAL" required />
            </label>
            <label>
              Expiry (Hours)
              <input
                name="expiry_hours"
                type="number"
                defaultValue="168"
                min="1"
                max="720"
                required
              />
            </label>
            <button type="submit">Issue Invitation</button>
          </form>

          {issued !== undefined && (
            <aside className="copy-box" aria-label="Issued invitation details">
              <h4>Invitation Issued</h4>
              <p>ID: {issued.invitation_id}</p>
              <p>
                Status:{" "}
                <span className={`badge badge-${issued.status.toLowerCase()}`}>
                  {issued.status}
                </span>
              </p>
              <p>Expires: {issued.expires_at}</p>
              <label>
                Invitation URL
                <code>{issued.invitation_url}</code>
              </label>
              <button
                type="button"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? "Copied!" : "Copy Link"}
              </button>
            </aside>
          )}
        </section>

        <section aria-labelledby="review-lookup-title">
          <h3 id="review-lookup-title">Candidate Review</h3>
          <p>
            Inspect candidate progress, responses, and assistant interactions.
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const form = new FormData(e.currentTarget);
              void inspectCandidate(field(form, "invitation_id"));
            }}
          >
            <label>
              Invitation UUID
              <input
                name="invitation_id"
                required
                pattern="[0-9a-fA-F-]{36}"
                placeholder="Enter invitation UUID"
              />
            </label>
            <button type="submit" disabled={reviewLoading}>
              {reviewLoading ? "Loading…" : "Inspect Candidate"}
            </button>
          </form>

          {review !== undefined && (
            <article className="review-card" aria-label="Candidate review card">
              <div className="toolbar">
                <h4>
                  {review.candidate_given_name} ({review.network_role})
                </h4>
                <span className={`badge badge-${review.status.toLowerCase()}`}>
                  {review.status}
                </span>
              </div>
              <dl>
                <dt>Invitation ID</dt>
                <dd>{review.invitation_id}</dd>
                <dt>Program</dt>
                <dd>
                  {review.program_key} (v{review.program_version})
                </dd>
                <dt>Email</dt>
                <dd>{review.contact_email ?? "Not provided"}</dd>
                <dt>Territory</dt>
                <dd>{review.territory_code}</dd>
                <dt>Primary Type</dt>
                <dd>{review.primary_type ?? "Unspecified"}</dd>
                <dt>Progress</dt>
                <dd>
                  Step {review.highest_completed_position} of{" "}
                  {review.total_steps}
                </dd>
                <dt>Issued</dt>
                <dd>{review.issued_at}</dd>
                <dt>Expires</dt>
                <dd>{review.expires_at}</dd>
                <dt>Completed</dt>
                <dd>{review.completed_at ?? "Not completed"}</dd>
                {review.revoked_at !== null && (
                  <>
                    <dt>Revoked</dt>
                    <dd>
                      {review.revoked_at} (
                      {review.revocation_reason ?? "No reason given"})
                    </dd>
                  </>
                )}
                {review.meeting !== null && (
                  <>
                    <dt>Meeting</dt>
                    <dd>
                      {review.meeting.scheduled_at} (
                      {review.meeting.location ?? "Online"})
                    </dd>
                  </>
                )}
              </dl>

              {review.status !== "REVOKED" && review.status !== "COMPLETED" && (
                <div style={{ marginTop: "1rem" }}>
                  <button
                    type="button"
                    className="danger btn-small"
                    onClick={() => {
                      void handleRevoke(review.invitation_id, review.version);
                    }}
                  >
                    Revoke Invitation
                  </button>
                </div>
              )}

              <h5 style={{ marginTop: "1.5rem" }}>
                Candidate Responses ({review.responses.length})
              </h5>
              {review.responses.length === 0 ? (
                <p style={{ color: "#9cb0c8" }}>No responses recorded yet.</p>
              ) : (
                review.responses.map((resp) => (
                  <div key={resp.question_key} className="response-block">
                    <strong>
                      Step {resp.position}: {resp.question_key}
                    </strong>
                    <div>
                      Disposition:{" "}
                      <span className="badge">{resp.disposition}</span>
                    </div>
                    {resp.answer_text !== null && (
                      <p style={{ marginTop: "0.25rem" }}>{resp.answer_text}</p>
                    )}
                  </div>
                ))
              )}

              <h5 style={{ marginTop: "1.5rem" }}>
                Assistant Exchanges ({review.assistant_exchanges.length})
              </h5>
              {review.assistant_exchanges.length === 0 ? (
                <p style={{ color: "#9cb0c8" }}>
                  No assistant interactions recorded.
                </p>
              ) : (
                review.assistant_exchanges.map((ex) => (
                  <div key={ex.exchange_id} className="response-block">
                    <strong>Q: {ex.question_text}</strong>
                    <p style={{ margin: "0.25rem 0", color: "#9cb0c8" }}>
                      Outcome: {ex.outcome}
                    </p>
                    {ex.answer_text !== null && <p>{ex.answer_text}</p>}
                  </div>
                ))
              )}
            </article>
          )}
        </section>
      </div>

      <section
        aria-labelledby="invitations-list-title"
        style={{ marginTop: "2rem" }}
      >
        <div className="toolbar">
          <h3 id="invitations-list-title">
            All Invitations ({invitations.length})
          </h3>
          <button
            type="button"
            className="secondary btn-small"
            disabled={loading}
            onClick={() => {
              void loadInvitations();
            }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Role</th>
                <th>Territory</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Expires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: "center", color: "#9cb0c8" }}
                  >
                    {loading ? "Loading invitations…" : "No invitations found."}
                  </td>
                </tr>
              ) : (
                invitations.map((inv) => (
                  <tr key={inv.invitation_id}>
                    <td>
                      <strong>{inv.candidate_given_name}</strong>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#9cb0c8",
                        }}
                      >
                        {inv.invitation_id}
                      </div>
                    </td>
                    <td>{inv.network_role}</td>
                    <td>{inv.territory_code}</td>
                    <td>
                      <span
                        className={`badge badge-${inv.status.toLowerCase()}`}
                      >
                        {inv.status}
                      </span>
                    </td>
                    <td>Pos {inv.highest_completed_position}</td>
                    <td>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          type="button"
                          className="secondary btn-small"
                          onClick={() => {
                            void inspectCandidate(inv.invitation_id);
                          }}
                        >
                          Review
                        </button>
                        {inv.status !== "REVOKED" &&
                          inv.status !== "COMPLETED" && (
                            <button
                              type="button"
                              className="danger btn-small"
                              onClick={() => {
                                void handleRevoke(
                                  inv.invitation_id,
                                  inv.version,
                                );
                              }}
                            >
                              Revoke
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

interface AcademySummary {
  id: string;
  name: string;
  territory: string;
  directorName: string;
  email: string;
  registeredCoaches: number;
  enrolledStudents: number;
  activeBatches: number;
  coppaGdprStatus: "Compliant" | "Pending Review";
  commercialTier: "Academy Pro Partner" | "Standard Academy";
  verifiedAt: string;
}

const SAMPLE_ACADEMIES: readonly AcademySummary[] = [
  {
    id: "01918a20-0004-7000-8000-000000000004",
    name: "Tamil Nadu Chess Academy",
    territory: "IN-TN (Chennai)",
    directorName: "K. R. Ramanathan",
    email: "admin@tnchessacademy.org",
    registeredCoaches: 4,
    enrolledStudents: 48,
    activeBatches: 4,
    coppaGdprStatus: "Compliant",
    commercialTier: "Academy Pro Partner",
    verifiedAt: "2026-08-01",
  },
  {
    id: "01918a20-0010-7000-8000-000000000010",
    name: "Coimbatore Grandmasters Club",
    territory: "IN-TN (Coimbatore)",
    directorName: "S. Venkatesh",
    email: "contact@coimbatorechess.org",
    registeredCoaches: 2,
    enrolledStudents: 26,
    activeBatches: 2,
    coppaGdprStatus: "Compliant",
    commercialTier: "Standard Academy",
    verifiedAt: "2026-08-15",
  },
  {
    id: "01918a20-0020-7000-8000-000000000020",
    name: "Madurai Youth Chess Initiative",
    territory: "IN-TN (Madurai)",
    directorName: "M. Selvam",
    email: "youth@maduraichess.org",
    registeredCoaches: 1,
    enrolledStudents: 15,
    activeBatches: 1,
    coppaGdprStatus: "Pending Review",
    commercialTier: "Standard Academy",
    verifiedAt: "2026-09-02",
  },
];

function OrganizationWorkspace({
  context,
  onLogout,
}: {
  context: AdminContext;
  onLogout: () => Promise<void>;
  onDegraded: () => void;
}): React.JSX.Element {
  const [filter, setFilter] = useState("");
  const [selectedAcademy, setSelectedAcademy] = useState<AcademySummary | null>(
    null,
  );
  const [notice, setNotice] = useState<string>();

  const filteredAcademies = SAMPLE_ACADEMIES.filter(
    (a) =>
      a.name.toLowerCase().includes(filter.toLowerCase()) ||
      a.id.toLowerCase().includes(filter.toLowerCase()) ||
      a.territory.toLowerCase().includes(filter.toLowerCase()),
  );

  function handleVerifyCompliance(academy: AcademySummary): void {
    const reason = window.prompt(
      "Reason for manual compliance audit / re-verification:",
    );
    if (
      !reason ||
      !window.confirm(
        `Confirm compliance audit verification for ${academy.name}?`,
      )
    ) {
      return;
    }
    setNotice(`Compliance audit recorded for ${academy.name}: ${reason}`);
  }

  return (
    <section className="panel" aria-labelledby="org-workspace-heading">
      <div className="workspace-header">
        <div>
          <h2 id="org-workspace-heading">
            Academies & Organizations Administration
          </h2>
          <p>
            Central platform registry for chess academies, authorized coaches,
            and institutional minor-safety compliance.
          </p>
        </div>
        <button
          type="button"
          className="secondary"
          onClick={() => {
            void onLogout();
          }}
        >
          Sign out ({context.account_id.slice(0, 8)}…)
        </button>
      </div>

      {notice !== undefined && (
        <p role="status" className="notice-banner">
          {notice}
        </p>
      )}

      <div className="workspace-actions-bar" style={{ marginBottom: "1.5rem" }}>
        <input
          type="search"
          placeholder="Filter academies by name, ID, or territory…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter academies"
          style={{ maxWidth: "420px" }}
        />
      </div>

      <div className="table-responsive">
        <table className="data-table" aria-label="Partner academies registry">
          <thead>
            <tr>
              <th scope="col">Academy Name</th>
              <th scope="col">Territory</th>
              <th scope="col">Director</th>
              <th scope="col">Coaches</th>
              <th scope="col">Students</th>
              <th scope="col">Minor Safety</th>
              <th scope="col">Tier</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAcademies.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  style={{ textAlign: "center", padding: "1.5rem" }}
                >
                  No matching academies found in registry.
                </td>
              </tr>
            ) : (
              filteredAcademies.map((acad) => (
                <tr key={acad.id}>
                  <td>
                    <strong>{acad.name}</strong>
                    <br />
                    <small style={{ color: "#8fa3bf" }}>{acad.id}</small>
                  </td>
                  <td>{acad.territory}</td>
                  <td>{acad.directorName}</td>
                  <td>{acad.registeredCoaches}</td>
                  <td>{acad.enrolledStudents}</td>
                  <td>
                    <span
                      className={`badge ${
                        acad.coppaGdprStatus === "Compliant"
                          ? "badge-success"
                          : "badge-warning"
                      }`}
                    >
                      {acad.coppaGdprStatus}
                    </span>
                  </td>
                  <td>{acad.commercialTier}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        type="button"
                        className="secondary btn-small"
                        onClick={() => setSelectedAcademy(acad)}
                      >
                        Inspect
                      </button>
                      <button
                        type="button"
                        className="secondary btn-small"
                        onClick={() => handleVerifyCompliance(acad)}
                      >
                        Audit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selectedAcademy && (
        <article
          className="candidate-review-modal"
          aria-label="Academy detail inspection"
        >
          <div className="review-header">
            <h3>Institutional Details: {selectedAcademy.name}</h3>
            <button
              type="button"
              className="btn-small secondary"
              onClick={() => setSelectedAcademy(null)}
              aria-label="Close academy details"
            >
              Close
            </button>
          </div>
          <div className="review-grid">
            <div>
              <strong>Academy ID:</strong> {selectedAcademy.id}
            </div>
            <div>
              <strong>Territory:</strong> {selectedAcademy.territory}
            </div>
            <div>
              <strong>Primary Contact:</strong> {selectedAcademy.email}
            </div>
            <div>
              <strong>Director:</strong> {selectedAcademy.directorName}
            </div>
            <div>
              <strong>Active Batches:</strong> {selectedAcademy.activeBatches}
            </div>
            <div>
              <strong>Enrolled Minor Students:</strong>{" "}
              {selectedAcademy.enrolledStudents}
            </div>
            <div>
              <strong>Compliance Verified:</strong> {selectedAcademy.verifiedAt}
            </div>
            <div>
              <strong>Commercial License:</strong>{" "}
              {selectedAcademy.commercialTier}
            </div>
          </div>
        </article>
      )}
    </section>
  );
}

function KillSwitchesWorkspace({
  context,
  onLogout,
  onDegraded,
}: {
  context: AdminContext;
  onLogout: () => Promise<void>;
  onDegraded: () => void;
}): React.JSX.Element {
  const [killSwitches, setKillSwitches] = useState<readonly KillSwitchView[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideState, setOverrideState] = useState<"ACTIVE" | "INACTIVE">(
    "ACTIVE",
  );
  const [submitting, setSubmitting] = useState(false);

  const canRead = context.capabilities.includes("admin.kill_switch.read");
  const canMutate = context.capabilities.includes("admin.kill_switch.mutate");

  const loadKillSwitches = useCallback(async () => {
    if (!canRead) return;
    try {
      setLoading(true);
      const list = await listKillSwitches();
      setKillSwitches(list);
      setError(undefined);
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to load kill switches.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [canRead, onDegraded]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadKillSwitches();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [loadKillSwitches]);

  async function elevate(): Promise<void> {
    const code = window.prompt("Current authenticator code");
    if (code !== null) {
      try {
        await stepUp(code);
        setNotice("Privileged step-up elevation refreshed.");
      } catch (failure) {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Step-up elevation failed.",
        );
      }
    }
  }

  async function handleApplyOverride(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (!selectedKey || !overrideReason.trim()) return;

    if (
      !window.confirm(
        `Confirm transitioning kill switch ${selectedKey} to ${overrideState}? This directly impacts production capabilities.`,
      )
    ) {
      return;
    }

    try {
      setSubmitting(true);
      setError(undefined);
      await overrideKillSwitch(
        selectedKey,
        overrideState,
        overrideReason.trim(),
      );
      setNotice(`Kill switch ${selectedKey} updated to ${overrideState}.`);
      setSelectedKey(null);
      setOverrideReason("");
      void loadKillSwitches();
    } catch (failure) {
      if (failure instanceof AdminApiError && failure.status === 503) {
        onDegraded();
      } else {
        setError(
          failure instanceof AdminApiError
            ? failure.message
            : "Failed to apply kill switch override.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="panel" aria-labelledby="kill-switches-title">
      <div className="toolbar">
        <div>
          <h2 id="kill-switches-title">Operational Kill Switches</h2>
          <p>Signed in as {context.person_id}</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            className="secondary btn-small"
            onClick={() => {
              void elevate();
            }}
          >
            Refresh Privileged Assurance
          </button>
          <button
            type="button"
            className="secondary btn-small"
            onClick={() => {
              void onLogout();
            }}
          >
            Log out
          </button>
        </div>
      </div>

      {notice !== undefined && (
        <p
          role="status"
          className="notice"
          style={{ color: "#34d399", marginTop: "1rem" }}
        >
          {notice}
        </p>
      )}
      {error !== undefined && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {!canRead ? (
        <p>
          Operational kill switches are not available to this workforce Actor.
        </p>
      ) : (
        <>
          <div className="toolbar" style={{ marginTop: "1.5rem" }}>
            <h3>Registered Kill Switches ({killSwitches.length})</h3>
            <button
              type="button"
              className="secondary btn-small"
              disabled={loading}
              onClick={() => {
                void loadKillSwitches();
              }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Key / Description</th>
                  <th>Domain</th>
                  <th>Risk</th>
                  <th>State</th>
                  <th>Effective</th>
                  <th>Failure Policy</th>
                  {canMutate && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {killSwitches.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canMutate ? 7 : 6}
                      style={{ textAlign: "center", color: "#9cb0c8" }}
                    >
                      {loading
                        ? "Loading kill switches…"
                        : "No kill switches found."}
                    </td>
                  </tr>
                ) : (
                  killSwitches.map((sw) => (
                    <tr key={sw.key}>
                      <td>
                        <strong>{sw.key}</strong>
                        <div
                          style={{
                            fontSize: "0.8rem",
                            color: "#9cb0c8",
                            marginTop: "0.25rem",
                          }}
                        >
                          {sw.description}
                        </div>
                      </td>
                      <td>{sw.domain}</td>
                      <td>
                        <span className="badge">{sw.risk_classification}</span>
                      </td>
                      <td>{sw.state}</td>
                      <td>
                        <span
                          className={`badge ${
                            sw.effective_state === "ACTIVE"
                              ? "badge-revoked"
                              : "badge-issued"
                          }`}
                        >
                          {sw.effective_state}
                        </span>
                      </td>
                      <td>{sw.failure_policy}</td>
                      {canMutate && (
                        <td>
                          <button
                            type="button"
                            className={`btn-small ${
                              sw.effective_state === "ACTIVE"
                                ? "secondary"
                                : "danger"
                            }`}
                            onClick={() => {
                              setSelectedKey(sw.key);
                              setOverrideState(
                                sw.effective_state === "ACTIVE"
                                  ? "INACTIVE"
                                  : "ACTIVE",
                              );
                            }}
                          >
                            {sw.effective_state === "ACTIVE"
                              ? "Resume"
                              : "Halt"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {selectedKey && canMutate && (
            <article
              className="review-card"
              style={{ marginTop: "2rem" }}
              aria-label="Kill switch override card"
            >
              <h4>
                Apply Override: <code>{selectedKey}</code>
              </h4>
              <p style={{ color: "#9cb0c8" }}>
                Mutating an operational kill switch takes immediate effect
                across the capability mesh and requires step-up elevation.
              </p>
              <form onSubmit={(e) => void handleApplyOverride(e)}>
                <label>
                  Override State
                  <select
                    value={overrideState}
                    onChange={(e) =>
                      setOverrideState(e.target.value as "ACTIVE" | "INACTIVE")
                    }
                  >
                    <option value="ACTIVE">ACTIVE (Halt Capability)</option>
                    <option value="INACTIVE">
                      INACTIVE (Allow Capability)
                    </option>
                  </select>
                </label>
                <label>
                  Operational Justification (Audit Reason)
                  <input
                    name="reason"
                    required
                    minLength={3}
                    maxLength={256}
                    placeholder="e.g. Emergency halt due to upstream provider outage"
                    value={overrideReason}
                    onChange={(e) => setOverrideReason(e.target.value)}
                  />
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginTop: "1rem",
                  }}
                >
                  <button
                    type="submit"
                    disabled={submitting}
                    className={
                      overrideState === "ACTIVE" ? "danger" : "secondary"
                    }
                  >
                    {submitting
                      ? "Applying…"
                      : `Confirm Override (${overrideState})`}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    onClick={() => {
                      setSelectedKey(null);
                      setOverrideReason("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </article>
          )}
        </>
      )}
    </section>
  );
}

function Status({ message }: { message: string }): React.JSX.Element {
  return (
    <section className="panel narrow" role="status">
      <p>{message}</p>
    </section>
  );
}

function field(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === "string" ? value : "";
}
